import { useState } from 'react';

import { Eye, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useCreateStockSource,
  useDeleteStockSource,
  useStockSources,
  useTestStockSource,
  useTriggerStockSync,
  useUpdateStockSource,
} from '@/hooks/useStockSync';

import type { StockSourceRow, StockTestResult } from '@/lib/api/stock-sync.api';
import type { StockSourceConfig } from '@repo/shared-types';

interface FormState {
  id?: string;
  name: string;
  description: string;
  config: string;
  cookie_header: string;
  cookie_set: boolean;
  sync_interval_minutes: number;
  is_active: boolean;
}

const STARTER_CONFIG = `{
  "request": {
    "base_url": "https://example.com/stock",
    "fixed_params": {}
  },
  "pagination": { "param": "page", "start": 1, "page_size": 15, "max_pages": 1000, "delay_ms": 1000 },
  "auth_expiry": { "redirect_location_contains": "/login", "fail_on_empty_first_page": true },
  "parse": {
    "record_selector": { "regex": "(<div class=\\"card\\"[\\\\s\\\\S]*?)(?=<div class=\\"card\\"|<footer)", "must_contain": "" },
    "dedupe_by": "external_id",
    "require_field": "external_id",
    "fields": {
      "external_id": { "regex": "href=\\"/vehicle/([^\\"?]+)", "transform": "trim" },
      "price": { "regex": "(?:&#xA3;|£)\\\\s*([\\\\d,]+)", "transform": "price_int" }
    }
  },
  "field_map": { "external_id": "external_id", "price": "price" },
  "vehicle_type_default": "Car"
}`;

const EMPTY: FormState = {
  name: '',
  description: '',
  config: STARTER_CONFIG,
  cookie_header: '',
  cookie_set: false,
  sync_interval_minutes: 1440,
  is_active: true,
};

const textarea =
  'border-input bg-background ring-offset-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 font-mono text-xs focus-visible:ring-2 focus-visible:outline-none';

const STATUS_BADGE: Record<
  StockSourceRow['status'],
  { label: string; variant: 'secondary' | 'outline' | 'destructive' }
> = {
  idle: { label: 'Idle', variant: 'secondary' },
  syncing: { label: 'Syncing…', variant: 'outline' },
  needs_auth: { label: 'Needs auth', variant: 'destructive' },
  error: { label: 'Error', variant: 'destructive' },
};

function parseJson(value: string): unknown {
  if (!value.trim()) throw new Error('Config is required');
  try {
    return JSON.parse(value);
  } catch {
    throw new Error('Invalid JSON in config');
  }
}

/** Cookies are sent as name=value pairs — a bare value would be silently dropped. */
function validateCookie(value: string): string {
  const trimmed = value.trim();
  if (trimmed && !trimmed.includes('=')) {
    throw new Error(
      'Cookie must be in "name=value" format, e.g. CustomerPortal.Production=<value> — you pasted only the value',
    );
  }
  return trimmed;
}

export const StockSourcesPage = () => {
  const { data: sources = [], isLoading } = useStockSources();
  const createMut = useCreateStockSource();
  const updateMut = useUpdateStockSource();
  const deleteMut = useDeleteStockSource();
  const testMut = useTestStockSource();
  const syncMut = useTriggerStockSync();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState<StockTestResult | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const openNew = () => {
    setForm(EMPTY);
    setError('');
    setTestResult(null);
    setOpen(true);
  };

  const openEdit = (source: StockSourceRow) => {
    setForm({
      id: source.id,
      name: source.name,
      description: source.description ?? '',
      config: JSON.stringify(source.config, null, 2),
      cookie_header: '',
      cookie_set: source.cookie_set,
      sync_interval_minutes: source.sync_interval_minutes,
      is_active: source.is_active,
    });
    setError('');
    setTestResult(null);
    setOpen(true);
  };

  const handleSave = async () => {
    setError('');
    try {
      const config = parseJson(form.config) as StockSourceConfig;
      const cookie = validateCookie(form.cookie_header);
      const payload = {
        name: form.name,
        description: form.description,
        config,
        sync_interval_minutes: form.sync_interval_minutes,
        is_active: form.is_active,
        // Only send the cookie when the user typed one — an empty field on edit
        // means "keep the stored cookie".
        ...(cookie ? { cookie_header: cookie } : {}),
      };
      if (form.id) await updateMut.mutateAsync({ id: form.id, data: payload });
      else await createMut.mutateAsync(payload as never);
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    }
  };

  const handleTest = async () => {
    setError('');
    setTestResult(null);
    try {
      const config = parseJson(form.config) as StockSourceConfig;
      const cookie = validateCookie(form.cookie_header);
      const result = await testMut.mutateAsync({
        config,
        ...(cookie ? { cookie_header: cookie } : {}),
        max_pages: 2,
      });
      setTestResult(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Test failed');
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Stock Sources</h1>
          <p className="text-muted-foreground text-sm">
            Declarative site-inventory scrapers that sync a dealer&apos;s full stock into the
            database on a schedule. Agents search it via a custom tool pinned to one source.
          </p>
        </div>
        <Button onClick={openNew}>
          <Plus className="mr-2 h-4 w-4" /> New Source
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Base URL</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last sync</TableHead>
                <TableHead>Interval</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={6}>Loading…</TableCell>
                </TableRow>
              )}
              {!isLoading && sources.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground">
                    No stock sources yet. Create one to start syncing a site&apos;s inventory.
                  </TableCell>
                </TableRow>
              )}
              {sources.map((source) => {
                const badge = STATUS_BADGE[source.status] ?? STATUS_BADGE.idle;
                return (
                  <TableRow key={source.id}>
                    <TableCell>
                      <div className="font-medium">{source.name}</div>
                      <div className="text-muted-foreground max-w-md truncate text-xs">
                        {source.description}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate text-xs">
                      {source.config?.request?.base_url}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                        {!source.is_active && <Badge variant="outline">Inactive</Badge>}
                        {!source.cookie_set && (
                          <span className="text-muted-foreground text-xs">no cookie</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {source.last_sync_completed_at ? (
                        <>
                          <div>
                            {new Date(source.last_sync_completed_at).toLocaleString('en-GB')}
                          </div>
                          <div className="text-muted-foreground">
                            {source.last_sync_count ?? 0} item(s)
                          </div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">never</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {source.sync_interval_minutes >= 1440
                        ? `${Math.round(source.sync_interval_minutes / 1440)}d`
                        : `${source.sync_interval_minutes}m`}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Sync now"
                        disabled={source.status === 'syncing' || syncMut.isPending}
                        onClick={() => syncMut.mutate(source.id)}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" title="Details" asChild>
                        <Link to={`/stock-sync/${source.id}`}>
                          <Eye className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Edit"
                        onClick={() => openEdit(source)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Delete"
                        onClick={() => {
                          if (
                            confirm(
                              `Delete stock source "${source.name}"? All its synced stock items are deleted with it.`,
                            )
                          )
                            deleteMut.mutate(source.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit stock source' : 'New stock source'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Name (snake_case)</label>
                <Input value={form.name} onChange={(e) => set('name', e.target.value)} />
              </div>
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <label className="text-sm font-medium">Sync interval (minutes)</label>
                  <Input
                    type="number"
                    min={15}
                    value={form.sync_interval_minutes}
                    onChange={(e) => set('sync_interval_minutes', Number(e.target.value) || 1440)}
                  />
                </div>
                <label className="flex items-center gap-2 pb-2 text-sm">
                  <Switch checked={form.is_active} onCheckedChange={(v) => set('is_active', v)} />
                  Active
                </label>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Description</label>
              <textarea
                className={textarea}
                rows={2}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-medium">
                Session cookie header{' '}
                {form.cookie_set && (
                  <span className="text-muted-foreground font-normal">
                    (one is stored — leave empty to keep it)
                  </span>
                )}
              </label>
              <textarea
                className={textarea}
                rows={2}
                placeholder="CustomerPortal.Production=…  (only for login-gated sites; stored encrypted, never shown again)"
                value={form.cookie_header}
                onChange={(e) => set('cookie_header', e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-medium">Config (StockSourceConfig JSON)</label>
              <textarea
                className={textarea}
                rows={16}
                value={form.config}
                onChange={(e) => set('config', e.target.value)}
              />
            </div>

            {/* Test */}
            <div className="bg-muted/40 space-y-2 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Test (crawls 2 pages, no DB writes)</label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTest}
                  disabled={testMut.isPending}
                >
                  {testMut.isPending ? 'Running…' : 'Run test'}
                </Button>
              </div>
              {testResult && (
                <div className="space-y-2">
                  <div className="text-muted-foreground text-xs">
                    {testResult.auth_expired
                      ? `Auth expired / login page detected: ${testResult.error}`
                      : `${testResult.records_parsed} record(s) across ${testResult.pages_fetched} page(s)${
                          testResult.error ? ` — ${testResult.error}` : ''
                        }`}
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-medium">Field coverage</div>
                    <pre className="bg-background max-h-40 overflow-auto rounded border p-2 text-xs">
                      {JSON.stringify(testResult.field_coverage, null, 2)}
                    </pre>
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-medium">Sample records</div>
                    <pre className="bg-background max-h-64 overflow-auto rounded border p-2 text-xs">
                      {JSON.stringify(testResult.sample_records, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={createMut.isPending || updateMut.isPending}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
